import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { DefaultValues, Resolver, useForm } from 'react-hook-form';
import {
  ATTRIBUTE_TYPES,
  AttributeResponse,
  AttributeType,
  DISPLAY_FORMAT_TYPES,
  isDisplayFormatAllowed,
  useCreateAttribute,
  useUpdateAttribute,
} from '@/entities/attributes';
import { useHasPermission } from '@/entities/profile';
import { useFormCodeGeneration } from '@/shared/hooks';
import { useAppNavigate } from '@/shared/lib';
import { AttributeFormValues, useAttributeSchema } from './validation';
import { useFormGuardActions } from '@/entities/formGuard';

const SELECTABLE_TYPES: AttributeType[] = [ATTRIBUTE_TYPES.SELECT, ATTRIBUTE_TYPES.MULTISELECT];

type Params = {
  initial?: AttributeResponse;
  onSuccess?: () => void;
  searchParams?: URLSearchParams;
};

export const useAttributeForm = (params: Params) => {
  const { initial, onSuccess, searchParams } = params;
  const navigate = useAppNavigate();
  const allowAdmin = useHasPermission('ADMIN_ATTRIBUTE');
  const schema = useAttributeSchema();
  const { resetFormGuard } = useFormGuardActions();

  const isEdit = allowAdmin && !!initial;
  const isDisabled = !allowAdmin || initial?.type === 'SYSTEM' || initial?.isDeleted;
  // Удалённый атрибут доступен только для восстановления (при праве администрирования)
  const canRestore = allowAdmin && !!initial?.isDeleted;

  // Тип данных и признак «список» описывают форму хранения значения, поэтому закрываются
  // с появлением первого значения. Факт наличия значений известен только серверу — берём его признак,
  // а не собственную догадку. Блокировка формы (системный/удалённый атрибут) сильнее этого условия
  const canEditStorageFields = !isDisabled && (!initial || initial.hasValues === false);
  // Превращение атрибута в структуру и обратно запрещено — у структуры тип закрыт всегда
  const isDataTypeEditable = canEditStorageFields && initial?.dataType !== ATTRIBUTE_TYPES.STRUCTURE;
  const isListEditable = canEditStorageFields;

  const defaults = useMemo(() => {
    // Get activeTab from searchParams for new attributes
    const activeTab = searchParams?.get('activeTab');
    const defaultAttributeGroups = initial?.attributeGroups?.map((g) => ({ code: g.code })) ?? [];

    // If creating new attribute and activeTab exists, set it as default group
    const attributeGroups = !initial && activeTab ? [{ code: activeTab }] : defaultAttributeGroups;

    return {
      name: initial?.name ?? '',
      code: initial?.code ?? '',
      description: initial?.description ?? '',
      tooltip: initial?.tooltip ?? '',
      // Тип данных при создании не предзаполняется: выбор делает пользователь, без выбора форма не сохраняется
      dataType: initial?.dataType,
      type: initial?.type ?? null,
      rule: initial?.rule ?? null,
      isList: initial?.isList ?? false,
      displayFormat: initial?.displayFormat ?? null,
      selectValues: initial?.selectValues
        ? initial.selectValues
            .filter((v) => !v.isDeleted)
            .map((v) => ({
              name: v.name,
              description: v.description || '',
              code: v.code,
            }))
        : undefined,
      attributeGroups,
      structure: initial?.structure
        ? {
            code: initial.structure.code,
            name: initial.structure.name,
            description: initial.structure.description ?? null,
            isSelect: initial.structure.isSelect ?? false,
            attributes:
              initial.structure.attributes?.map((attr) => ({
                code: attr.code,
                name: attr.name ?? null,
                dataType: attr.dataType ?? null,
                isList: attr.isList ?? null,
                description: attr.description ?? null,
                type: attr.type ?? null,
                rule: attr.rule ?? null,
                tooltip: attr.tooltip ?? '',
                isNew: false,
              })) ?? [],
            groups:
              initial.structure.groups?.map((group) => ({
                items: Object.fromEntries(
                  Object.entries(group.items).map(([key, item]) => [
                    key,
                    {
                      code: item.code ?? '',
                      value: item.value,
                      description: item.description ?? null,
                    },
                  ]),
                ),
              })) ?? null,
          }
        : null,
    } satisfies DefaultValues<AttributeFormValues>;
  }, [initial, searchParams]);

  const form = useForm<AttributeFormValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema) as Resolver<AttributeFormValues>,
  });

  // Use code generation hook
  const { isCodeLocked, isGeneratingCode, handleNameBlur } = useFormCodeGeneration({
    getName: () => form.getValues('name'),
    getCode: () => form.getValues('code'),
    setCode: (code) => form.setValue('code', code),
    entityType: 'attribute',
    isEdit,
  });

  useEffect(() => {
    form.reset(defaults);
  }, [form, defaults]);

  // Clear structure when type changes to non-STRUCTURE
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name !== 'dataType') return;

      if (value.dataType !== ATTRIBUTE_TYPES.STRUCTURE) {
        form.setValue('structure', null);
      }
      // Формат отображения принадлежит типу: несочетаемая с новым типом пара не сохраняется
      if (!isDisplayFormatAllowed(value.dataType, value.displayFormat)) {
        form.setValue('displayFormat', null);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const { mutateAsync: createAttribute, isPending: isCreating } = useCreateAttribute();
  const { mutateAsync: updateAttribute, isPending: isUpdating } = useUpdateAttribute();
  const isLoading = isCreating || isUpdating;

  const currentType = form.watch('dataType');
  const isSelectable = SELECTABLE_TYPES.includes(currentType);
  const isStructure = currentType === ATTRIBUTE_TYPES.STRUCTURE;
  const isDisplayFormatSupported = DISPLAY_FORMAT_TYPES.includes(currentType as (typeof DISPLAY_FORMAT_TYPES)[number]);

  const onSubmit = form.handleSubmit(async (values) => {
    // Clean up empty structure - if structure exists but has no code, name, or attributes, set it to null
    const cleanedStructure =
      values.structure && (!values.structure.code || !values.structure.name || !values.structure.attributes?.length)
        ? null
        : values.structure;

    const cleanedValues = { ...values, structure: cleanedStructure };

    // Check if displayFormat is applicable for this type
    const isDisplayFormatApplicable = DISPLAY_FORMAT_TYPES.includes(
      cleanedValues.dataType as (typeof DISPLAY_FORMAT_TYPES)[number],
    );

    // Пустое поле «Подсказка» в режиме редактирования трактуется как null (сбрасывает текущее значение в БД, см. IL фаза 5)
    const tooltipForUpdate = cleanedValues.tooltip?.trim() ? cleanedValues.tooltip : null;

    // Для STRUCTURE-родителя пробрасываем structure.attributes[*].tooltip — единственный канал обновления подсказок вложенных при PUT
    const structureForUpdate =
      isStructure && cleanedValues.structure
        ? {
            code: cleanedValues.structure.code ?? '',
            name: cleanedValues.structure.name ?? '',
            attributes:
              cleanedValues.structure.attributes?.map((attr) => ({
                code: attr.code ?? '',
                tooltip: attr.tooltip?.trim() ? attr.tooltip : null,
              })) ?? [],
          }
        : undefined;

    if (isEdit) {
      await updateAttribute({
        code: initial!.code,
        attribute: {
          code: initial!.code,
          name: cleanedValues.name,
          description: cleanedValues.description,
          // Закрытые поля уходят со своими сохранёнными значениями — сервер видит, что они не менялись
          dataType: isDataTypeEditable ? cleanedValues.dataType : initial!.dataType,
          isList: isListEditable ? cleanedValues.isList : initial!.isList,
          type: cleanedValues.type || undefined,
          rule: cleanedValues.rule || undefined,
          displayFormat: isDisplayFormatApplicable ? cleanedValues.displayFormat : undefined,
          selectValues: isSelectable ? cleanedValues.selectValues : undefined,
          attributeGroups: cleanedValues.attributeGroups
            ? cleanedValues.attributeGroups.map((item: { code: string }) => ({ code: item.code }))
            : undefined,
          tooltip: tooltipForUpdate,
          structure: structureForUpdate,
        },
      });
      // Отправленное становится сохранённым: карточка перестаёт считать себя изменённой
      form.reset(cleanedValues);
      resetFormGuard();
    } else {
      // В режиме создания пустая «Подсказка» — не отправляем поле, чтобы сработала LLM-генерация на бэке (см. IL фаза 2)
      const tooltipForCreate = cleanedValues.tooltip?.trim() ? cleanedValues.tooltip : undefined;

      await createAttribute({
        code: cleanedValues.code,
        name: cleanedValues.name,
        dataType: cleanedValues.dataType,
        type: cleanedValues.type || undefined,
        rule: cleanedValues.rule || undefined,
        description: cleanedValues.description,
        tooltip: tooltipForCreate,
        isList: cleanedValues.isList ?? undefined,
        displayFormat: isDisplayFormatApplicable ? cleanedValues.displayFormat : undefined,
        selectValues: SELECTABLE_TYPES.includes(cleanedValues.dataType) ? cleanedValues.selectValues : undefined,
        attributeGroups: cleanedValues.attributeGroups
          ? cleanedValues.attributeGroups.map((item: { code: string }) => ({ code: item.code }))
          : undefined,
        structure:
          isStructure && cleanedValues.structure
            ? {
                code: cleanedValues.structure.code ?? '',
                name: cleanedValues.structure.name ?? '',
                description: cleanedValues.structure.description ?? null,
                isSelect: cleanedValues.structure.isSelect ?? false,
                attributes:
                  cleanedValues.structure.attributes?.map((attr) => ({
                    code: attr.code ?? '',
                    name: attr.name ?? null,
                    dataType: attr.dataType ?? null,
                    isList: attr.isList ?? null,
                    description: attr.description ?? null,
                    type: attr.type ?? null,
                    rule: attr.rule ?? null,
                    tooltip: attr.tooltip?.trim() ? attr.tooltip : null,
                  })) ?? [],
                groups:
                  cleanedValues.structure.groups?.map((group) => ({
                    items: Object.fromEntries(
                      Object.entries(group.items).map(([key, item]) => [
                        key,
                        {
                          code: item.code ?? '',
                          value: item.value,
                          description: item.description ?? null,
                        },
                      ]),
                    ),
                  })) ?? null,
              }
            : undefined,
      });
      // Открываем созданный атрибут на вкладке его первой группы, прочие параметры адреса сохраняем.
      // Без групп вкладка не задаётся — открывается «Все»
      const preservedParams = Object.fromEntries(Array.from(searchParams ?? []).filter(([key]) => key !== 'activeTab'));
      const activeTab = cleanedValues.attributeGroups?.[0]?.code;

      form.reset(cleanedValues);
      resetFormGuard();
      navigate(
        '/attributes/:id',
        { id: cleanedValues.code },
        { replace: true, query: { ...preservedParams, activeTab } },
      );
    }

    onSuccess?.();
  });

  // Handle title change on blur - save immediately if editing
  const handleTitleSave = async (value: string) => {
    form.setValue('name', value);

    if (isEdit && value !== initial?.name) {
      resetFormGuard();
      await updateAttribute({
        code: initial!.code,
        attribute: {
          code: initial!.code,
          name: value,
          description: initial!.description,
          dataType: initial!.dataType,
        },
      });
    }
  };

  return {
    form,
    isEdit,
    isLoading,
    isDisabled,
    canRestore,
    isSelectable,
    isStructure,
    currentType,
    isDisplayFormatSupported,
    isDataTypeEditable,
    isListEditable,
    onSubmit,
    handleTitleSave,
    handleNameBlur,
    isCodeLocked,
    isGeneratingCode,
  } as const;
};
